import React from 'react';
import { createPortal } from 'react-dom';

import { EventNames, eventBus } from '../../utils/eventBus';
import { X } from 'lucide-react';

interface DriveDropdownFilters {
  filterItemType: string;
  filterPeriod: string;
  filterDocType: string;
  filterCompany: string | string[];
  setFilterItemType: (
    value: 'all' | 'document' | 'saved-message' | 'snippet' | 'studio-result' | 'shared' | 'private'
  ) => void;
  setFilterPeriod: (value: string) => void;
  setFilterDocType: (value: string) => void;
  setFilterCompany: (value: string | string[]) => void;
}

interface DriveFilterDropdownProps {
  theme: ReturnType<typeof import('../../contexts/ThemeContext').useTheme>['theme'];
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  menuRef: React.RefObject<HTMLDivElement>;
  filters: DriveDropdownFilters;
  uniqueDocTypes: string[];
  uniqueCompanies: string[];
  uniquePeriods: string[];
  activeFiltersCount: number;
  onClearFilters: () => void;
  onClose: () => void;
}

const DriveFilterDropdown: React.FC<DriveFilterDropdownProps> = ({
  theme,
  isOpen,
  anchorRef,
  menuRef,
  filters,
  uniqueDocTypes,
  uniqueCompanies,
  uniquePeriods,
  activeFiltersCount,
  onClearFilters,
  onClose,
}) => {
  if (!isOpen || !anchorRef.current) return null;

  const style = (() => {
    const buttonRect = anchorRef.current!.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const estimatedHeight = 600;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const shouldOpenUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    return {
      [shouldOpenUp ? 'bottom' : 'top']: shouldOpenUp
        ? `${viewportHeight - buttonRect.top + 8}px`
        : `${buttonRect.bottom + 8}px`,
      left: `${buttonRect.left}px`,
      maxHeight: '600px',
      overflowY: 'auto' as const,
    };
  })();

  const handleClear = () => {
    filters.setFilterItemType('all');
    filters.setFilterPeriod('all');
    filters.setFilterDocType('all');
    filters.setFilterCompany('all');
    eventBus.emit(EventNames.DRIVE_FILTERS_TO_MENTIONS, {});
    onClearFilters();
    onClose();
  };

  const companyValue = Array.isArray(filters.filterCompany) ? 'multiple' : filters.filterCompany;

  return createPortal(
    <div
      ref={menuRef}
      className={`fixed ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg shadow-xl z-[9999] py-2 min-w-[280px] max-w-[320px]`}
      style={style}
    >
      <div className="px-3 py-2">
        <div className={`text-xs font-semibold ${theme.colors.text.secondary} mb-2`}>
          Filtros Principales
        </div>

        <div className="mb-2">
          <select
            value={filters.filterItemType}
            onChange={(e) =>
              filters.setFilterItemType(
                e.target.value as
                  | 'all'
                  | 'document'
                  | 'saved-message'
                  | 'snippet'
                  | 'studio-result'
                  | 'shared'
                  | 'private'
              )
            }
            className={`w-full px-3 py-2 rounded-md ${theme.colors.bg.input} ${theme.colors.text.primary} border ${theme.colors.border.primary} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">📁 Todos</option>
            <option value="shared">🔗 Compartidos</option>
            <option value="private">🔒 Privados</option>
            <option value="snippet">✂️ Snippets</option>
            <option value="saved-message">💬 Mensajes</option>
            <option value="studio-result">✨ Resultados Studio</option>
          </select>
        </div>
      </div>

      <div className={`border-t ${theme.colors.border.primary} my-2`} />

      <div className="px-3 py-2">
        <div className={`text-xs font-semibold ${theme.colors.text.secondary} mb-2`}>
          Filtros Adicionales
        </div>

        <div className="mb-2">
          <select
            value={companyValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'all') {
                filters.setFilterCompany('all');
              } else if (value === 'multiple') {
                return;
              } else {
                filters.setFilterCompany(value);
              }
            }}
            className={`w-full px-3 py-2 rounded-md ${theme.colors.bg.input} ${theme.colors.text.primary} border ${theme.colors.border.primary} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">🏢 Todas las empresas</option>
            {Array.isArray(filters.filterCompany) && filters.filterCompany.length > 1 && (
              <option value="multiple" disabled>
                {filters.filterCompany.length} empresas seleccionadas:{' '}
                {filters.filterCompany.join(', ')}
              </option>
            )}
            {uniqueCompanies.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
          {Array.isArray(filters.filterCompany) && filters.filterCompany.length > 1 && (
            <div className={`mt-1 text-xs ${theme.colors.text.secondary}`}>
              Filtrado por: {filters.filterCompany.join(', ')}
            </div>
          )}
        </div>

        <div className="mb-2">
          <select
            value={filters.filterPeriod}
            onChange={(e) => filters.setFilterPeriod(e.target.value)}
            className={`w-full px-3 py-2 rounded-md ${theme.colors.bg.input} ${theme.colors.text.primary} border ${theme.colors.border.primary} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">📅 Todos los periodos</option>
            {uniquePeriods.map((period) => (
              <option key={period} value={period}>
                {period}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-2">
          <select
            value={filters.filterDocType}
            onChange={(e) => filters.setFilterDocType(e.target.value)}
            className={`w-full px-3 py-2 rounded-md ${theme.colors.bg.input} ${theme.colors.text.primary} border ${theme.colors.border.primary} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">📋 Todos los tipos</option>
            {uniqueDocTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      </div>

      {activeFiltersCount > 0 && (
        <>
          <div className={`border-t ${theme.colors.border.primary} my-2`} />
          <div className="px-3 pb-2">
            <button
              onClick={handleClear}
              className={`w-full px-3 py-2 rounded-md bg-nb-error/20 text-nb-error hover:bg-nb-error/30 transition-colors text-sm font-medium flex items-center justify-center gap-2`}
            >
              <span>Limpiar todos los filtros</span>
            </button>
          </div>
        </>
      )}
    </div>,
    document.body
  );
};

export default DriveFilterDropdown;
